import { QueryClient } from '@tanstack/react-query';

// Create a client with optimized settings for supervision management
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 1000 * 60 * 5,
      // Keep unused data in cache for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Retry failed requests 2 times
      retry: 2,
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Query keys for consistent cache management
export const queryKeys = {
  // Supervision queries
  supervisions: {
    all: ['supervisions'] as const,
    lists: () => [...queryKeys.supervisions.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.supervisions.lists(), filters] as const,
    paginated: (params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      filters?: Record<string, any>;
    }) => [...queryKeys.supervisions.all, 'paginated', params] as const,
    details: () => [...queryKeys.supervisions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.supervisions.details(), id] as const,
    employee: (employeeId: string) => [...queryKeys.supervisions.all, 'employee', employeeId] as const,
    patient: (patientId: string) => [...queryKeys.supervisions.all, 'patient', patientId] as const,
  },
  
  // Employee queries  
  employees: {
    all: ['employees'] as const,
    lists: () => [...queryKeys.employees.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.employees.lists(), filters] as const,
    details: () => [...queryKeys.employees.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.employees.details(), id] as const,
  },
  
  // Patient queries
  patients: {
    all: ['patients'] as const, 
    lists: () => [...queryKeys.patients.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.patients.lists(), filters] as const,
    details: () => [...queryKeys.patients.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.patients.details(), id] as const,
  },
  
  // User queries
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: ['users', 'profile'] as const,
  },
  
  // Supervision Rules
  supervisionRules: {
    all: ['supervisionRules'] as const,
    lists: () => [...queryKeys.supervisionRules.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.supervisionRules.lists(), filters] as const,
  },
} as const;

export default queryClient;
